import { useState, useMemo } from 'react';
import { CheckCircle2, XCircle, FlaskConical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Parameter } from '@shared/types';
import { parseSingleParameter, identifyVariant } from '@/utils/parameterUtils';
import { ParameterInput } from '@/components/parameter/ParameterInput';

export function ParserTester() {
  const [input, setInput] = useState('');
  const [previewParam, setPreviewParam] = useState<Parameter | null>(null);

  const result = useMemo(() => {
    const trimmed = input.trim();
    if (!trimmed) return null;
    const param = parseSingleParameter(trimmed);
    if (!param) return { matched: false as const };
    return { matched: true as const, param, variant: identifyVariant(param) };
  }, [input]);

  const handleCommit = (_: Parameter, value: Parameter['value']) => {
    if (result?.matched) {
      setPreviewParam((prev) =>
        prev ? { ...prev, value } : { ...result.param, value },
      );
    }
  };

  // Keep previewParam in sync when input changes
  const displayParam =
    previewParam && result?.matched
      ? { ...result.param, value: previewParam.value }
      : result?.matched
        ? result.param
        : null;

  // Reset preview state when input changes
  const handleInputChange = (v: string) => {
    setInput(v);
    setPreviewParam(null);
  };

  const isCamera = displayParam?.type === 'camera_pip';
  const isSvgGroup =
    displayParam?.type === 'svgcheckboxgroup' ||
    displayParam?.type === 'svgradiogroup';

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Live Parser Tester</h2>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Type any parameter definition to see how Meshpit parses it and which
        input widget it maps to.
      </p>

      <Input
        value={input}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder="height = 42; // [5:200]"
        className="font-mono text-xs"
        spellCheck={false}
        autoComplete="off"
      />

      {/* Result */}
      {result && (
        <div className="mt-3">
          {result.matched ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                <span className="text-muted-foreground">Matched:</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {result.variant}
                </span>
                {result.param.type && (
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    type: {result.param.type}
                  </code>
                )}
              </div>

              {/* Live preview of the input widget */}
              {isCamera || isSvgGroup ? (
                <p className="text-xs italic text-muted-foreground">
                  {isCamera
                    ? 'Camera PiP preview requires a 3D model in the editor.'
                    : 'SVG group preview requires an SVG file attachment.'}
                </p>
              ) : (
                displayParam && (
                  <div className="rounded-md border border-border bg-background p-3">
                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Input Preview
                    </p>
                    <ParameterInput
                      param={displayParam}
                      handleCommit={handleCommit}
                    />
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <XCircle className="h-4 w-4 shrink-0 text-destructive" />
              <span>
                Not a recognized parameter definition. Try:{' '}
                <code className="font-mono">name = value; // comment</code>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
